import React, { useState, useEffect, useContext } from 'react';
import { Card, Form, Button, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import PageHeader1 from '../../components/common/PageHeader1';
import apiClient from "../../utils/api";
import moment from 'moment';
import DateRangePicker from 'react-bootstrap-daterangepicker';
import 'bootstrap-daterangepicker/daterangepicker.css';
import {connect, useSelector} from 'react-redux';

function ReportsList() {
    const { user } = useSelector((state) => state.auth);
    const isDriver = user?.roles?.includes('Driver');

    const [reports, setReports] = useState([]);
    const [parkingLots, setParkingLots] = useState([]);
    const [drivers, setDrivers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [filters, setFilters] = useState({
        report_type: 'daily_report', // Set default to daily_report for drivers
        date_range: {
            startDate: moment(),
            endDate: moment()
        },
        parking_lot_id: '',
        driver_id: ''
    });

    useEffect(() => {
        if (isDriver) {
            // Set driver_id filter to the logged-in driver's ID
            const driverId = user.id; // Assuming the user object contains the driver's ID
            setFilters(prev => ({
                ...prev,
                driver_id: driverId
            }));
            // Automatically fetch reports when component mounts
            fetchReports();
        }
        fetchDropdownData();
    }, [isDriver, user]);

    const fetchDropdownData = async () => {
        try {
            const token = localStorage.getItem('auth_token');
            const [parkingLotsRes, driversRes] = await Promise.all([
                apiClient.get('accounts/reports/parking-lots',{
                    headers: { Authorization: `Bearer ${token}`
                }}),
                apiClient.get('accounts/reports/drivers',{
                    headers: { Authorization: `Bearer ${token}`
                }})
            ]);
            setParkingLots(parkingLotsRes.data.data);
            setDrivers(driversRes.data.data);
        } catch (error) {
            console.error('Error:', error);
            toast.error('Error loading filter data');
        }
    };

    const fetchReports = async () => {
        try {
            setIsLoading(true);
            const token = localStorage.getItem('auth_token');
            const response = await apiClient.get('accounts/reports', { 
                params: {
                    ...filters,
                    start_date: filters.date_range.startDate.format('YYYY-MM-DD'),
                    end_date: filters.date_range.endDate.format('YYYY-MM-DD')
                },
                headers: { Authorization: `Bearer ${token}` }
            });
            setReports(response.data.data);
        } catch (error) {
            console.error('Error:', error);
            toast.error('Error loading reports');
        } finally {
            setIsLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleDateChange = (event, picker) => {
        setFilters(prev => ({
            ...prev,
            date_range: {
                startDate: picker.startDate,
                endDate: picker.endDate
            }
        }));
    };

    const handleExport = async (format) => {
        try {
            const endpoint = format === 'excel' 
                ? 'accounts/reports/export-excel'
                : 'accounts/reports/export-pdf';

            const exportParams = {
                report_type: filters.report_type,
                start_date: filters.date_range.startDate.format('YYYY-MM-DD'),
                end_date: filters.date_range.endDate.format('YYYY-MM-DD'),
                parking_lot_id: filters.parking_lot_id || '',
                driver_id: filters.driver_id || ''
            };

            const response = await apiClient.get(endpoint, {
                responseType: 'blob',
                params: exportParams,
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                }
            });

            // Create a more descriptive filename
            const reportTypeText = filters.report_type === 'valet_location' ? 'valet_location' : 
                                  filters.report_type === 'income' ? 'income' : 'daily_report';
            const dateRange = `${filters.date_range.startDate.format('YYYY-MM-DD')}_to_${filters.date_range.endDate.format('YYYY-MM-DD')}`;
            const filename = `${reportTypeText}_report_${dateRange}.${format === 'excel' ? 'xlsx' : 'pdf'}`;

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            
            // Clean up the URL object
            window.URL.revokeObjectURL(url);
            
            toast.success(`${format.toUpperCase()} exported successfully!`);
        } catch (error) {
            console.error('Error:', error);
            toast.error(`Error exporting ${format.toUpperCase()}`);
        }
    };

    // Format the parking type for better display
    const formatParkingType = (parkingType) => {
        if (!parkingType || parkingType === 'N/A') return 'N/A';
        
        // Replace underscores with spaces and capitalize each word
        return parkingType
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    return (
        <div className="body d-flex py-3">
            <div className="container-xxl">
                <PageHeader1 pagetitle='Reports' />
                
                <Card className="mb-3">
                    <Card.Body>                            
                        <div className="row g-3">
                                <div className="col-md-3">
                                    <Form.Group>
                                        <Form.Label>Report Type</Form.Label>
                                        <Form.Select
                                            name="report_type"
                                            value={filters.report_type}
                                            onChange={handleFilterChange}
                                        >
                                            {!isDriver && (
                                                <>
                                                <option value="valet_location">Valet Location Report</option>
                                                <option value="income">Income Report</option>
                                                </>
                                            )}
                                            {isDriver && (
                                                <option value="daily_report">Daily Report</option>
                                            )}
                                        </Form.Select>
                                    </Form.Group>
                                </div>
                                
                                {!isDriver && (
                                    <div className="col-md-3">
                                        <Form.Group>
                                            <Form.Label>Date Range</Form.Label>
                                            <DateRangePicker
                                                initialSettings={{
                                                    startDate: filters.date_range.startDate,
                                                    endDate: filters.date_range.endDate,
                                                    ranges: {
                                                        'Today': [moment(), moment()],
                                                        'Yesterday': [moment().subtract(1, 'days'), moment().subtract(1, 'days')],
                                                        'Last 7 Days': [moment().subtract(6, 'days'), moment()],
                                                        'Last 30 Days': [moment().subtract(29, 'days'), moment()],
                                                        'This Month': [moment().startOf('month'), moment().endOf('month')],
                                                        'Last Month': [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')]
                                                    }
                                                }}
                                                onApply={handleDateChange}
                                            >
                                                <div className="position-relative">
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        value={`${filters.date_range.startDate.format('MMMM D, YYYY')} - ${filters.date_range.endDate.format('MMMM D, YYYY')}`}
                                                        readOnly
                                                    />
                                                </div>
                                            </DateRangePicker>
                                        </Form.Group>
                                    </div>
                                )}

                                <div className="col-md-3">
                                    <Form.Group>
                                        <Form.Label>Valet Locations</Form.Label>
                                        <Form.Select
                                            name="parking_lot_id"
                                            value={filters.parking_lot_id}
                                            onChange={handleFilterChange}
                                        >
                                            <option value="">All Valet Locations</option>
                                            {parkingLots.map(lot => (
                                                <option key={lot.id} value={lot.id}>
                                                    {lot.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </div>

                                
                                <div className="col-md-3">
                                    <Form.Group>
                                        <Form.Label>Driver</Form.Label>
                                        <Form.Select
                                            name="driver_id"
                                            value={filters.driver_id}
                                            onChange={handleFilterChange}
                                        >
                                            <option value="">All Drivers</option>
                                            {drivers.map(driver => (
                                                <option key={driver.id} value={driver.id}>
                                                    {driver.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </div>
                            </div>

                        <div className="row mt-3">
                            <div className="col-12">
                                <Button 
                                    variant="primary" 
                                    className="me-2"
                                    onClick={fetchReports}
                                    disabled={isLoading}
                                >
                                    {isLoading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                            Searching...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fa fa-search me-2"></i>
                                            Search
                                        </>
                                    )}
                                </Button>

                                <Button 
                                    variant="success" 
                                    className="me-2"
                                    onClick={() => handleExport('pdf')}
                                    disabled={isLoading || reports.length === 0}
                                    title={reports.length === 0 ? "No data available to export" : "Export current report data as PDF"}
                                >
                                    <i className="fa fa-file-pdf me-2"></i>
                                    Export PDF {reports.length === 0 && '(No Data)'}
                                </Button>
                                <Button 
                                    variant="info"
                                    onClick={() => handleExport('excel')}
                                    disabled={isLoading || reports.length === 0}
                                    title={reports.length === 0 ? "No data available to export" : "Export current report data as Excel"}
                                >
                                    <i className="fa fa-file-excel me-2"></i>
                                    Export Excel {reports.length === 0 && '(No Data)'}
                                </Button>
                            </div>
                        </div>
                    </Card.Body>
                </Card>

                <Card>
                    <Card.Body>                    <Table responsive striped hover>
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    {filters.report_type === 'valet_location' && (
                                        <>
                                            <th>Location Name</th>
                                            <th>Total Parking</th>
                                            <th>Filled Parking</th>
                                            <th>Vacated Parking</th>
                                            <th>Assigned Drivers</th>
                                            <th>Total Income</th>
                                        </>
                                    )}
                                    {filters.report_type === 'employee' && (
                                        <>
                                            <th>Driver Name</th>
                                            <th>Check In</th>
                                            <th>Check Out</th>
                                            <th>Total Parkings</th>
                                            <th>Incidents</th>
                                            <th>Total Income</th>
                                        </>
                                    )}
                                    {(filters.report_type === 'income' || filters.report_type === 'daily_report') && (
                                        <>
                                            <th>Location Name</th>
                                            <th>Parking Type</th>
                                            <th>Total Parking</th>
                                            <th>Filled Parking</th>
                                            <th>Empty Parking</th>
                                            <th>Total Income</th>
                                        </>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {reports.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="text-center py-4">
                                            {isLoading ? (
                                                <>
                                                    <div className="spinner-border text-primary mb-2" role="status">
                                                        <span className="visually-hidden">Loading...</span>
                                                    </div>
                                                    <div>Loading reports...</div>
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fa fa-file-text fa-3x text-muted mb-3"></i>
                                                    <div className="text-muted">No reports found for the selected criteria.</div>
                                                    <div className="text-muted small">Try adjusting your filters and search again.</div>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                ) : (
                                    reports.map((report, index) => (
                                        <tr key={index}>
                                            <td>{new Date(report.date).toLocaleDateString()}</td>
                                            {filters.report_type === 'valet_location' && (
                                                <>
                                                    <td>{report.location_name}</td>
                                                    <td>{report.total_spots}</td>
                                                    <td>{report.filled_parking}</td>
                                                    <td>{report.vacated_parking}</td>
                                                    <td>{report.assigned_drivers}</td>
                                                    <td>QAR {report.total_income}</td>
                                                </>
                                            )}
                                            {filters.report_type === 'employee' && (
                                                <>
                                                    <td>{report.driver_name}</td>
                                                    <td>{report.check_in}</td>
                                                    <td>{report.check_out}</td>
                                                    <td>{report.total_parkings}</td>
                                                    <td>{report.incidents}</td>
                                                    <td>QAR {report.total_income}</td>
                                                </>
                                            )}
                                            {(filters.report_type === 'income' || filters.report_type === 'daily_report') && (
                                                <>
                                                    <td>{report.location_name}</td>
                                                    <td>{formatParkingType(report.parking_type)}</td>
                                                    <td>{report.total_spots}</td>
                                                    <td>{report.filled_spots}</td>
                                                    <td>{report.empty_spots}</td>
                                                    <td>QAR {report.total_income}</td>
                                                </>
                                            )}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </Table>
                    </Card.Body>
                </Card>
            </div>
        </div>
    );
}

export default ReportsList;